/**
 * Fusionner des entrees en double (« Merge duplicates » de Dashlane et de Proton Pass) : la Sante du coffre rapproche
 * des copies d'un meme compte (`vault-health.ts`, `duplicateGroups`) ; la fusion en garde UNE et y reunit ce que les
 * autres portaient en propre, pour que les jeter ne perde rien :
 *   - adresses, etiquettes, passkeys (sans doublon `rpId` + `credentialId`) et historique des mots de passe : reunis ;
 *   - notes : celles de la copie gardee, puis chaque note differente des autres, a la suite ;
 *   - champs personnalises : reunis sans doublon (meme nom, meme valeur) ;
 *   - toute autre valeur absente de la copie gardee (graine 2FA, numero de carte...) : reprise ;
 *   - une valeur PRESENTE des deux cotes mais differente (deux graines 2FA) : celle de la copie gardee reste, l'autre
 *     devient un champ MASQUE « <libelle> (copie fusionnee) » -- jamais ecrasee en silence ;
 *   - re-demande du mot de passe maitre : gardee si UNE copie l'avait (une fusion ne baisse jamais la protection).
 * Le nom et l'identifiant (meme compte, a la casse pres) restent ceux de la copie gardee.
 *
 * Refus (`null`) plutot que perte : si la reunion des champs depasse `MAX_CUSTOM_FIELDS`, la normalisation en
 * jetterait ; on ne fusionne pas. Module pur, zero E/S : `VaultStore.mergeEntries` le compose (re-chiffrement,
 * pieces jointes, corbeille des copies).
 */
import type { EntryCustomField, EntrySecrets } from '@klavox/crypto-core';
import { normalizeUriMatch, uriMatchRuleOf, type UriMatchRule } from '@klavox/vault-model';
import { MAX_CUSTOM_FIELDS, normalizeCustomFields, readCustomFields } from './custom-fields.js';
import { readPasskeys, withPasskeys } from './passkey-credential.js';
import { normalizePasswordHistory, readPasswordHistory } from './password-history.js';
import { readTags, withTags } from './entry-tags.js';

/** Suffixe du champ masque qui garde la valeur divergente d'une copie. */
export const MERGED_FIELD_SUFFIX = ' (copie fusionnee)';

/** Cles traitees a part (reunies, ou propres a la copie gardee) ; drapeaux d'etat jamais repris des copies jetees. */
const HANDLED_KEYS = new Set([
  'name',
  'username',
  'uris',
  'uriMatch',
  'notes',
  'customFields',
  'passkeys',
  'passwordHistory',
  // Date du mot de passe de la copie gardee : celle d'une copie jetee decrirait un autre mot de passe.
  'passwordChangedAt',
  'itemHistory',
  // Echeance du mot de passe de la copie gardee : celle d'une copie jetee viserait un autre mot de passe.
  'passwordExpiry',
  'passwordRenewDays',
  'tags',
  // Identifiants d'entrees : reportes par `VaultStore.mergeEntries` (`withMergedLinks`), qui connait les copies jetees.
  'links',
  'reprompt',
  'archived',
  'trashedAt',
  'healthExcluded',
  'healthIgnored',
]);

/** Libelles lisibles des valeurs divergentes les plus courantes. */
const CONFLICT_LABELS: Readonly<Record<string, string>> = { password: 'Mot de passe', totpSecret: 'Graine 2FA' };

function isEmpty(value: unknown): boolean {
  return value === undefined || value === null || (typeof value === 'string' && value.trim().length === 0) || (Array.isArray(value) && value.length === 0);
}

function asText(value: unknown): string {
  return typeof value === 'string' ? value : JSON.stringify(value);
}

/**
 * Secrets de la copie gardee, enrichis de ceux des autres. `null` si la fusion perdrait un champ personnalise
 * (plafond `MAX_CUSTOM_FIELDS`).
 */
export function mergeDuplicateSecrets(kept: EntrySecrets, others: readonly EntrySecrets[]): EntrySecrets | null {
  const merged: Record<string, unknown> = { ...kept };
  const extraFields: EntryCustomField[] = [];

  const uris: string[] = [];
  const seenUris = new Set<string>();
  for (const uri of [kept, ...others].flatMap((secrets) => secrets.uris ?? [])) {
    const key = uri.trim();
    if (key.length === 0 || seenUris.has(key)) continue;
    seenUris.add(key);
    uris.push(uri);
  }
  if (uris.length > 0) merged['uris'] = uris;
  // Chaque adresse garde sa regle de correspondance, celle de la copie gardee d'abord.
  const rules = new Map<string, UriMatchRule | undefined>();
  for (const uri of uris) rules.set(uri, [kept, ...others].map((secrets) => uriMatchRuleOf(secrets, uri)).find((rule) => rule !== undefined));
  const uriMatch = normalizeUriMatch(uris, rules);
  if (uriMatch !== undefined) merged['uriMatch'] = uriMatch;
  else delete merged['uriMatch'];

  const notes: string[] = [];
  for (const note of [kept, ...others].map((secrets) => (typeof secrets.notes === 'string' ? secrets.notes.trim() : ''))) {
    if (note.length > 0 && !notes.some((existing) => existing.includes(note))) notes.push(note);
  }
  if (notes.length > 0) merged['notes'] = notes.join('\n\n');

  for (const other of others) {
    for (const [key, value] of Object.entries(other)) {
      if (HANDLED_KEYS.has(key) || isEmpty(value)) continue;
      const current = merged[key];
      if (isEmpty(current)) merged[key] = value;
      else if (asText(current) !== asText(value)) {
        extraFields.push({ name: `${CONFLICT_LABELS[key] ?? key}${MERGED_FIELD_SUFFIX}`, value: asText(value), hidden: true });
      }
    }
  }

  const fields: EntryCustomField[] = [];
  const seenFields = new Set<string>();
  for (const field of [...[kept, ...others].flatMap(readCustomFields), ...extraFields]) {
    const key = JSON.stringify([field.name, field.value, field.kind ?? null]);
    if (seenFields.has(key)) continue;
    seenFields.add(key);
    fields.push(field);
  }
  if (fields.length > MAX_CUSTOM_FIELDS) return null;
  if (fields.length > 0) merged['customFields'] = normalizeCustomFields(fields);

  if ([kept, ...others].some((secrets) => secrets['reprompt'] === true)) merged['reprompt'] = true;

  const history = normalizePasswordHistory(
    [kept, ...others].flatMap(readPasswordHistory),
    typeof kept.password === 'string' ? kept.password : '',
  );
  if (history.length > 0) merged['passwordHistory'] = history;
  else delete merged['passwordHistory'];

  const withKeys = withPasskeys(merged as EntrySecrets, [kept, ...others].flatMap(readPasskeys));
  return withTags(withKeys, [kept, ...others].flatMap(readTags));
}
